import * as React from 'react';

import modalsConfig, { MODALS } from './config';
import {
  IConfigItem,
  IModalsProviderProps,
  IModalsProviderState,
  IWithModalProps,
} from './interfaces';
import { Modals } from './Modals';

export interface IModalData {
  caption: string;
  text: string;
  onConfirm: () => void;
}
export const ModalsContext = React.createContext<{
  openModal(name: MODALS, data: IModalData): void;
  closeModal(name: MODALS): void;
}>({} as any);

const generateInitialState = (config: IConfigItem[]): IModalsProviderState => {
  return config.reduce((acc: IModalsProviderState, item: IConfigItem) => {
    acc[item.name] = false;
    return acc;
  }, {} as IModalsProviderState);
};

export class ModalsProvider extends React.Component<
  IModalsProviderProps,
  IModalsProviderState
> {
  constructor(props: IModalsProviderProps) {
    super(props);

    this.state = generateInitialState(modalsConfig);
  }

  public render() {
    const { children } = this.props;

    return (
      <ModalsContext.Provider
        value={{
          closeModal: this.closeModal,
          openModal: this.openModal,
        }}
      >
        {children}
        <Modals modals={this.state} closeModal={this.closeModal} />
      </ModalsContext.Provider>
    );
  }

  private readonly openModal = (
    modalName: string,
    data = null as unknown,
    modalOptions = {},
  ) => {
    this.setModal({
      [modalName]: {
        data,
        isOpen: true,
        modalOptions,
      },
    });
  };

  private readonly closeModal = (modalName: MODALS) => {
    this.setModal({
      [modalName]: {
        data: null,
        isOpen: false,
        modalOptions: {},
      },
    });
  };

  private readonly setModal = (data: Partial<IModalsProviderState>) => {
    this.setState(data as IModalsProviderState);
  };
}

export function withModals<T, S>(
  Component: React.ComponentClass<T & IWithModalProps, S>,
): React.ComponentType<T> {
  return (props: T) => (
    <ModalsContext.Consumer>
      {({ openModal, closeModal }) => (
        <Component {...props} openModal={openModal} closeModal={closeModal} />
      )}
    </ModalsContext.Consumer>
  );
}

export function withModalsFC<T>(
  Component: React.FC<T & IWithModalProps>,
): React.FC<T> {
  return (props: T) => (
    <ModalsContext.Consumer>
      {({ openModal, closeModal }) => (
        <Component {...props} openModal={openModal} closeModal={closeModal} />
      )}
    </ModalsContext.Consumer>
  );
}
